import {AfterViewInit, Component, computed, inject, Input, input, OnDestroy, OnInit, output, signal} from '@angular/core';
import {BaseDataService} from "../../../../core/services/base-data.service";
import {CssPropertiesEnum} from "../../../../core/models/css-properties.enum";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {CssPropertyPipe} from "../../../../core/pipes/css-property.pipe";
import {TranslationService} from "../../../../core/services/translation.service";
import {GeneralWarningComponent} from "../../../../core/components/general-warning/general-warning.component";
import {Bonus, BonusType} from "../../../../data/models/bonus.model";
import {Offer} from "../../../../data/models/offer.model";
import {
  bonusAddressTranslationKey,
  bonusDifferentAddressTranslationKey,
  configFlag,
  CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS
} from "../../../../core/utils/digital-order-address";
import {ImageService} from "../../../../core/services/image.service";
import {BonusDetailComponent} from "../bonus-detail/bonus-detail.component";
import {MatDialog} from "@angular/material/dialog";
import {MatCheckbox} from "@angular/material/checkbox";
import {VHostService} from "../../../../core/services/v-host.service";
import {MatCard, MatCardHeader, MatCardImage, MatCardTitle} from "@angular/material/card";
import {NgClass, NgOptimizedImage} from "@angular/common";
import {MatStepper, MatStepperPrevious} from "@angular/material/stepper";
import {ConfigService} from "../../../../core/services/config.service";
import {OrderProcessingService} from "../../../../data/services/order-processing.service";
import {OfferService} from "../../../../data/services/offer.service";
import {MatButton} from "@angular/material/button";
import {LoadingSpinnerComponent} from "../../../../core/components/loading-spinner/loading-spinner.component";
import {MatError, MatFormField, MatHint, MatInput, MatLabel} from "@angular/material/input";
import {
  HupSubscriptionAddressComponent
} from "../../../hup-subscription/hup-subscription-detail/hup-subscription-address/hup-subscription-address.component";
import {AddressFormValue, BonusFormValue, OrderState} from "../../../../data/models/subscription-order.model";
import {CustomValidators} from "../../../../core/validators";
import {HttpClient} from "@angular/common/http";
import {distinctUntilChanged, map, Subscription} from "rxjs";
import {
  formatAddressLines,
  resolveBonusAddress,
  resolveBonusAddressTarget
} from "../../../../core/utils/bonus-address-resolution";
import {isAddressFormValueEmpty} from "../../../../core/utils/external-address-merge";

/**
 * Die Inhalts-Gruppen eines Prämien-Adressblocks, die mit dem Auf-/Zuklappen bzw. dem Wechsel des
 * Prämientyps aktiviert bzw. deaktiviert werden. Das Schalter-Control 'showAddress' gehört bewusst
 * nicht dazu — s. applyBonusAddressActivation(). In advertiserAddress fehlt 'userData'; fehlende
 * Gruppen werden übersprungen.
 */
const BONUS_CONTENT_GROUPS = ['address', 'person', 'userData'];

/**
 * Geldprämien werden auf ein Konto überwiesen und brauchen deshalb Zahldaten - die des Bestellers
 * wie die des Werbers ("Leser werben Leser").
 */
function isMoneyBonus(bonusType: BonusType): boolean {
  return bonusType === BonusType.MONEY_BONUS || bonusType === BonusType.THIRD_PARTY_MONEY_BONUS;
}

@Component({
  selector: 'app-bonus-step',
  imports: [
    CssPropertyPipe,
    GeneralWarningComponent,
    ReactiveFormsModule,
    MatCheckbox,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    NgOptimizedImage,
    NgClass,
    MatCardImage,
    MatStepperPrevious,
    MatButton,
    LoadingSpinnerComponent,
    MatError,
    MatFormField,
    MatHint,
    MatInput,
    MatLabel,
    HupSubscriptionAddressComponent,
  ],
  templateUrl: './bonus-step.component.html',
  styleUrl: './bonus-step.component.css'
})
export class BonusStepComponent implements OnInit, AfterViewInit, OnDestroy {
  protected orderProcessService = inject(OrderProcessingService);
  protected imageService = inject(ImageService);
  protected translationService = inject(TranslationService);
  protected vHostService = inject(VHostService);
  protected configService = inject(ConfigService);
  private offerService = inject(OfferService);
  private baseDataService = inject(BaseDataService);
  private http = inject(HttpClient);

  protected bonusDetailsDialog = inject(MatDialog);

  public bonuses = input<Bonus[]>();
  /** Das bestellte Angebot - hier nur für die Beschriftung der Prämienadresse gebraucht. */
  public offer = input<Offer>();
  protected bonusSelection = signal(false);
  protected isEditable = input<boolean>();
  protected currentProductAlreadyOrdered = input<boolean>();
  public isAuthenticated = input<boolean>();

  private formBuilder = new FormBuilder();
  protected bonusFormGroup: FormGroup;
  @Input() parentStepper!: MatStepper;

  protected showBonusPoints: boolean = false;
  protected maxBonuses: number = 6;
  protected bonusReload: number = 6;
  /** Ist eine Geldprämie gewählt? Dann wird sie auf ein Konto überwiesen - s. isMoneyBonus(). */
  protected moneyBonusSelected: boolean = false;
  /** Ist eine Sachprämie gewählt, die eine Zuzahlung verlangt? */
  protected nonMoneyBonusWithClientCoPaymentSelected: boolean = false;

  protected maxPoints: number = 12; // TODO: load
  protected usedPoints: number = 0;

  protected freeBonusPoints: number = this.maxPoints;

  protected selectedBonus: Bonus [] = [];

  /**
   * Die Adresse, an die die Prämie ohne abweichende Angabe geht (Liefer-, ersatzweise
   * Rechnungsadresse). Ist sie undefined, liegt noch gar keine Adresse vor - dann wird die
   * Prämienadress-Maske sofort aufgeklappt statt einer Kachel mit dem Lieferziel.
   */
  protected bonusAddressTarget = signal<AddressFormValue | undefined>(undefined);

  /**
   * Sobald der Nutzer die Checkbox selbst bedient hat, wird der Auf-/Zu-Zustand nicht mehr
   * automatisch nachgeführt - seine Entscheidung hat Vorrang vor dem Lieferziel.
   */
  private bonusAddressChoiceByUser = false;

  /** Die aktuell in der Maske "Abweichende Prämienadresse" erfasste Adresse. */
  private enteredBonusAddress = signal<AddressFormValue | undefined>(undefined);

  /**
   * Die Adresse, an die die Prämie tatsächlich geht - und damit das, was die Kachel zeigen muss:
   * die erfasste abweichende Adresse, sonst das Lieferziel. Dieselbe Regel, nach der der Converter
   * die Prämienadresse an das Bestell-Objekt hängt.
   */
  protected effectiveBonusAddress = computed(() =>
    resolveBonusAddress(this.enteredBonusAddress(), {deliveryAddress: this.bonusAddressTarget()}));

  /**
   * Die Anschrift der Prämienadresse als fertige Zeilen für die Kachel - mit Anrede.
   *
   * Im Formularwert steht die Anrede als Schlüssel ('1', 'FR'); die Kachel zeigt den Text aus den
   * Stammdaten, solange er dort zu finden ist.
   */
  protected bonusAddressTargetLines = computed(() =>
    formatAddressLines(this.effectiveBonusAddress(), key => this.salutationDescription(key)));

  /** Config 'digitalOrder.bonusAddressIsDeliveryAddress' - s. bonusAddressTitle(). */
  private bonusAddressIsDeliveryAddress = signal(false);

  /** Titel der Prämienadresse - bei digitalen Abos ggf. der der Lieferadresse. */
  protected bonusAddressTitle = computed(() => this.translationService.getTranslation(
    bonusAddressTranslationKey(this.offer()?.offerType, this.bonusAddressIsDeliveryAddress())));

  /**
   * Übersetzungs-Schlüssel der Checkbox "Abweichende Prämienadresse". Anders als der Titel wird hier
   * der Schlüssel selbst durchgereicht - die Adress-Komponente übersetzt ihn erst im Template.
   */
  protected bonusDifferentAddressKey = computed(() =>
    bonusDifferentAddressTranslationKey(this.offer()?.offerType, this.bonusAddressIsDeliveryAddress()));

  /** Config 'offer.showBonusAddress': Darf überhaupt eine abweichende Prämienadresse angegeben werden? */
  private allowDifferentBonusAddress = signal(true);

  /**
   * Sind alle angebotenen Prämien Sachprämien? Dann steht schon vor der Auswahl fest, dass die
   * Prämie an eine Anschrift geht - egal, welche der Nutzer nimmt.
   */
  private onlyNonMoneyBonusesOffered = computed(() => {
    const offered = this.bonuses() ?? [];
    return offered.length > 0 && offered.every(bonus => bonus?.bonusType === BonusType.NON_MONEY_BONUS);
  });

  /**
   * Wird der Prämienadress-Block gerendert?
   *
   * Sobald eine Prämie gewählt ist, entscheidet deren Art. Vorher nur dann, wenn ausschließlich
   * Sachprämien angeboten werden: Bei gemischten Prämienarten ist bis zur Auswahl offen, ob
   * überhaupt eine Prämienadresse gebraucht wird.
   */
  protected showBonusAddressBlock(): boolean {
    if (this.selectedBonus.length > 0) {
      return this.selectedBonus[0]?.bonusType === BonusType.NON_MONEY_BONUS;
    }
    return this.onlyNonMoneyBonusesOffered();
  }

  /**
   * Wird die Maske für die Prämienadresse gerendert?
   *
   * Eine ABWEICHENDE Adresse gibt es nur, wenn 'offer.showBonusAddress' sie erlaubt. Ohne bekanntes
   * Lieferziel ist diese Maske aber die einzige Quelle der Prämienadresse - dann erscheint sie auch
   * bei abgeschalteter Konfiguration, sonst ginge die Prämie nirgendwohin.
   */
  protected showBonusAddressForm(): boolean {
    return this.showBonusAddressBlock() && (!this.bonusAddressTarget() || this.allowDifferentBonusAddress());
  }

  /** Wird der Werber-Adressblock gerendert? Eine Fremdprämie muss dafür gewählt sein. */
  protected showAdvertiserAddressBlock(): boolean {
    return this.selectedBonus[0]?.bonusType === BonusType.THIRD_PARTY_NON_MONEY_BONUS;
  }

  public stepCompleted = output<boolean>();
  private readonly subscriptions = new Subscription();

  askForBirthday: boolean = true;
  isBirthdayRequired: boolean = false;

  /** Config 'offer.askForPhone': Wird an der Prämienadresse nach der Telefonnummer gefragt? */
  askForPhone: boolean = false;
  /** Config 'requiredFields.addr.phone': Ist die Telefonnummer der Prämienadresse Pflicht? */
  isPhoneRequired: boolean = false;

  askForPaymentHolder: boolean = false;

  ngOnInit(): void {
    this.handleConfigs();
    this.buildBonusForm();
    this.requireAddressFields(this.bonusAddressGroup);
    this.requireAddressFields(this.advertiserAddressGroup);

    //Beschreibungen laden
    this.bonuses().forEach((bonus: Bonus) => {
      this.offerService.getArticleDescriptionFromId(bonus.articleCode).subscribe(articleDescription => {
        bonus.descriptionFromDocument = articleDescription.articleDescription;
      });
    });

    // falls nur 1 Bonus vorhanden ist, soll dieser automatisch vorgewählt werden.
    if (this.bonuses().length > 0) {
      if (this.bonuses().length == 1) {
        this.onSelectBonus(this.bonuses()[0], null);
      }
      this.bonusSelection.set(true);
    }

    if (!this.bonusSelection()) {
      this.setValidatorsAtIBAN(this.moneyBonusSelected || this.nonMoneyBonusWithClientCoPaymentSelected);
    }

    this.subscriptions.add(this.bonusFormGroup.statusChanges.subscribe(status => {
      this.stepCompleted.emit(status === 'VALID');
    }));

    this.observeBonusAddressTarget();

    this.subscriptions.add(this.bonusAddressGroup.get('showAddress')
      ?.valueChanges.subscribe(() => {
        // Nur vom Nutzer ausgelöste Wechsel kommen hier an: das automatische Nachführen setzt den
        // Wert mit emitEvent:false.
        this.bonusAddressChoiceByUser = true;
        this.applyBonusAddressActivation();
      }));

    // Muss nach der Bonus-Vorauswahl oben stehen, damit selectedBonus hier gefüllt ist, und bewusst
    // vor connectForm: sonst schiebt schon der erste Neuberechnungs-Durchlauf einen künstlichen
    // bonusData-Wert in den Bestell-State, der dort bislang erst durch eine Nutzereingabe entsteht.
    this.applyBonusAddressActivation();
    // statusChanges liefert nur Status-Übergänge. Die Bonus-Vorauswahl (onSelectBonus) läuft vor
    // dieser Subscription, deshalb braucht der Parent-Step hier einmal explizit den aktuellen Stand.
    this.stepCompleted.emit(this.bonusFormGroup.status === 'VALID');

    // Bewusst kein connectForm: der Bestell-State bekommt nicht den rohen Formularwert, sondern die
    // Prämienadresse in der Form, in der sie auch wirklich gilt - s. publishBonusData().
    this.subscriptions.add(this.bonusFormGroup.valueChanges.subscribe(() => this.publishBonusData()));

    this.publishBonusData();
  }

  /**
   * Die Bankverbindung der Prämie, inklusive der BIC.
   *
   * Die BIC füllt der IBAN-Prüfer in ein deaktiviertes Feld - deaktivierte Controls fallen aus
   * form.value heraus, weshalb sie ohne den Rohwert nie am Bestell-Objekt ankäme. Solange der
   * Prüfer nichts eingetragen hat, steht dort der Platzhaltertext; der darf nicht als BIC
   * übertragen werden.
   */
  private bonusPaymentValue(): BonusFormValue['payment'] {
    const payment = this.bonusFormGroup.get('payment').getRawValue();
    if (payment?.bicBonus === this.translationService.getTranslation('filled_automatically')) {
      return {...payment, bicBonus: null};
    }
    return payment;
  }

  /**
   * Schreibt die Prämiendaten in den Bestell-State - inklusive der Prämienadresse, die tatsächlich
   * gilt: der abweichenden aus der Maske, sonst dem Lieferziel.
   *
   * Warum die Adresse hier aufgelöst wird und nicht erst im Converter: Der zugeklappte Adressblock
   * ist deaktiviert und fällt damit aus dem Formularwert heraus. Wer die Auflösung dem Converter
   * überlässt, verlässt sich darauf, dass dieser das Lieferziel im State wiederfindet - fehlt es
   * dort, hinge am Bestell-Objekt gar keine Prämienadresse. So steht immer genau das im State, was
   * der Nutzer in der Kachel sieht.
   *
   * Die Prämienadresse bekommt nur, wer die Prämie auch erhält: bei der Sachprämie der Besteller,
   * bei der Fremdprämie ("Leser werben Leser") der Werber über advertiserAddress, bei der
   * Geldprämie niemand - dort tritt die Bankverbindung an ihre Stelle. Ohne diese Weiche fiele
   * resolveBonusAddress auch für Fremd- und Geldprämien auf die Anschrift des Bestellers zurück,
   * und die Übersicht zeigte zwei Empfänger statt einem.
   *
   * Das Bestell-Objekt ist davon unberührt: der Converter löst die Empfängeradresse selbst auf
   * (s. convertBonusData) und bleibt damit auch für die Geldprämie bei der bisherigen Anschrift.
   *
   * Ohne gewählte Prämie wird nichts geschrieben, damit kein künstlicher Wert im State landet.
   * Ein initialer Aufruf ist nötig, weil eine in ngOnInit vorausgewählte Prämie keinen
   * valueChanges mehr erzeugt.
   */
  private publishBonusData(): void {
    const entered = this.bonusAddressGroup.value as AddressFormValue;
    this.enteredBonusAddress.set(entered);

    if (this.selectedBonus.length === 0) {
      return;
    }

    const bonusGoesToOrderer = this.selectedBonus[0]?.bonusType === BonusType.NON_MONEY_BONUS;

    const bonusData: BonusFormValue = {
      ...(this.bonusFormGroup.value as BonusFormValue),
      payment: this.bonusPaymentValue(),
      bonusAddress: bonusGoesToOrderer
        ? resolveBonusAddress(entered, {deliveryAddress: this.bonusAddressTarget()})
        : undefined
    };

    this.orderProcessService.update(state => ({
      ...state,
      bonusData
    }));
  }

  /**
   * Die Aktivierung wird nach dem Aufbau der Kind-Komponenten noch einmal durchgesetzt.
   *
   * HupSubscriptionAddressComponent haengt in ihrem Init Validatoren an die Adress-Controls und
   * schaltet die Gruppe je nach readonly-Zustand um - das laeuft NACH unserem ngOnInit. Ein
   * zugeklappter Praemienadress-Block konnte dadurch mit Pflichtfeldern aktiv bleiben: sichtbar war
   * nichts, der Schritt liess sich aber nicht verlassen. Ein erneuter Durchlauf ist gefahrlos, weil
   * applyContentGroupActivation nur bei echtem Zustandswechsel umschaltet.
   */
  ngAfterViewInit(): void {
    this.applyBonusAddressActivation();
    this.stepCompleted.emit(this.bonusFormGroup.status === 'VALID');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setValidatorsAtIBAN(value: boolean) {
    if (value === true) {
      this.bonusFormGroup.get('payment.ibanBonus').setValidators(Validators.required);
      this.bonusFormGroup.get('payment.bicBonus').setValidators(Validators.required);
      this.setAsyncValidators();
      // this.needIbanBic = true;
    } else {
      this.bonusFormGroup.get('payment.ibanBonus').clearAsyncValidators();
      this.bonusFormGroup.get('payment.ibanBonus').clearValidators();
      this.bonusFormGroup.get('payment.bicBonus').clearValidators();

      this.bonusFormGroup.get('payment').reset();
      // this.needIbanBic = false;
    }
  }

  private setAsyncValidators() {
    this.bonusFormGroup.get('payment.ibanBonus').setAsyncValidators(CustomValidators.validateIBAN(
      this.http,
      this.translationService,
      this.bonusFormGroup.get('payment.bicBonus')
    ));
  }

  buildBonusForm(): void {
    this.bonusFormGroup = this.formBuilder.group({
      bonus: new FormArray([], Validators.required),
      bonusAddress: this.formBuilder.group({
        userData: this.formBuilder.group({
          birthDay: [''],
          phone: [''],
          email: [''],
        }),
        showAddress: [''],
        address: this.formBuilder.group({
          country: [''],
          zipcode: [''],
          city: [''],
          street: [''],
          houseNo: [''],
          extraLine: [''],
          houseNoExt: [''],
          stiege: [''],
          stock: [''],
          tuer: [''],
          district: ['']
        }),
        person: this.formBuilder.group({
          title: [''],
          salutation: [''],
          firstName: [''],
          lastName: [''],
          initials: [''],
          middlename: [''],
        })
      }),
      advertiserAddress: this.formBuilder.group({
        address: this.formBuilder.group({
          country: [''],
          zipcode: [''],
          city: [''],
          street: [''],
          houseNo: [''],
          extraLine: [''],
          houseNoExt: [''],
          stiege: [''],
          stock: [''],
          tuer: [''],
          district: ['']
        }),
        person: this.formBuilder.group({
          title: [''],
          salutation: [''],
          firstName: [''],
          lastName: [''],
          initials: [''],
          middlename: [''],
        })
      }),
      payment: this.formBuilder.group({
        ibanBonus: ['', Validators.required],
        bicBonus: [{value: this.translationService.getTranslation('filled_automatically'), disabled: true}],
        accountHolderBonus: ['']
      })
    });
  }

  loadMoreBonuses(): void {
    this.maxBonuses = this.maxBonuses + this.bonusReload;
  }

  /**
   * beim Auswählen einer Prämie durch den Nutzer:
   * prüft, ob der Nutzer genügend Punkte hat, um sie auszuwählen. Falls ja, wird er ausgewählt (ermöglicht mehrere Prämien)
   * falls nein: Alle Prämien werden entfernt, und die gewünschte wird ausgewählt
   * hier ist ein Sonderfall zu beachten, wenn der Nutzer eine Prämie auswählt, die schon gewählt ist, soll natürlich nichts geschehen
   * @param bonus die ausgewählte Prämie
   * @param element html element der Prämie
   */
  onSelectBonus(bonus: Bonus, element: HTMLElement): void {
    if (!bonus.bonusValue || bonus.bonusValue < 1) {
      bonus.bonusValue = 12;
    }

    let found = this.isBonusAlreadySelected(bonus);
    //falls der bonus noch nicht ausgewählt ist
    if (!found) {
      // es gibt genügend freie punkte, um den bonus auszuwählen
      if (this.freeBonusPoints >= bonus.bonusValue) {
        this.addBonus(bonus);
      }
      //es gibt nicht genügend freie bonuspunkte
      else if (this.freeBonusPoints < bonus.bonusValue) {
        this.removeAllBonuses();
        this.addBonus(bonus);
      }
    }
    if (element) {
      element.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});
    }

    this.handleBirthdayField();
    this.applyBonusAddressActivation();
  }

  /**
   * prüft, ob das Geburtsdatum-Feld angezeigt werden und Pflichtfeld sein soll und setzt die entsprechenden Variablen.
   */
  private handleBirthdayField() {
    let creditCheckRequired = this.selectedBonus.some(bonus => bonus.creditCheckRequired === true);
    this.isBirthdayRequired = this.isBirthdayRequired || creditCheckRequired;
  }

  private handleConfigs() {
    //Soll die Prämienadresse als Lieferadresse bezeichnet werden (nur digitale Abos)?
    this.configService.loadConfig(CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS).subscribe(config => {
      this.bonusAddressIsDeliveryAddress.set(configFlag(config, false));
    });

    //Soll abweichende Angabe von der Prämienadresse erlaubt sein?
    // Vorgabe des Schlüssels ist '1'; ein fehlender oder unlesbarer Wert darf die Maske nicht
    // stillschweigend entfernen - s. configFlag().
    this.configService.loadConfig('offer.showBonusAddress').subscribe(config => {
      this.allowDifferentBonusAddress.set(configFlag(config, true));
      // Ob der Adressblock validiert wird, hängt jetzt an dieser Konfiguration - der Ladevorgang ist
      // asynchron und kommt in der Regel nach der ersten Auswertung in ngOnInit an. Antwortet der
      // Config-Cache dagegen sofort, sind wir noch vor buildBonusForm() - dann gibt es schlicht noch
      // nichts zu schalten, und die Auswertung am Ende von ngOnInit erledigt es.
      if (this.bonusFormGroup) {
        this.applyBonusAddressActivation();
      }
    });

    //Wie viele Prämien darf man maximal auswählen?
    this.configService.loadConfig('offer.maxBonusCount').subscribe(config => {
      if (config && config.value) {
        this.maxBonuses = Number(config.value);
        this.bonusReload = this.maxBonuses;
      }
    });

    this.configService.loadConfig('offer.askForBirthday').subscribe(config => {
      if (config && config.value) {
        this.askForBirthday = (config.value === 'true' || config.value === '1');
      }
    });

    this.configService.loadConfig('requiredFields.addr.birthday').subscribe(config => {
      if (config && config.value) {
        this.isBirthdayRequired = (config.value === 'true' || config.value === '1');
      }
    });

    // Telefonnummer an der Prämienadresse: abfragen / Pflicht? Beide Schlüssel löst der
    // ConfigResolverService der Route 'offer/:offerId' vorab auf, daher synchron per getConfig().
    this.askForPhone = configFlag(this.configService.getConfig('offer.askForPhone'), false);
    this.isPhoneRequired = configFlag(this.configService.getConfig('requiredFields.addr.phone'), false);

    this.configService.loadConfig('askForPaymentAccountHolder').subscribe(config => {
      if (config && config.value) {
        this.askForPaymentHolder = (config.value === 'true' || config.value === '1');
      }
    });
  }

  onShowDetails(bonus: Bonus): void {
    this.bonusDetailsDialog.open(BonusDetailComponent, {
      data: {bonus, showBonusPoints: false}
    });
  }

  getBonusControls(): AbstractControl[] {
    return (this.bonusFormGroup.get('bonus') as FormArray).controls;
  }

  /**
   * prüft, ob eine Prämie bereits ausgewählt ist
   * @param bonus
   * @private
   */
  private isBonusAlreadySelected(bonus: Bonus) {
    const control = new FormControl(bonus);
    let found = false;
    for (let data of this.getBonusControls()) {

      if (data.value == control.value) {
        found = true;
      }
    }
    return found;
  }

  /**
   * fügt eine Prämie zu den ausgewählten Prämien hinzu
   * @param bonus
   * @private
   */
  private addBonus(bonus: Bonus) {
    const control = new FormControl(bonus);
    (this.bonusFormGroup.get('bonus') as FormArray).push(control);
    this.selectedBonus.push(bonus);
    bonus.selected = true;
    this.updateBonusPaymentFlags();
    this.calcBonusPoints();
  }

  /**
   * Führt die Weichen für Zuzahlung und Zahldaten aus der aktuellen Auswahl nach.
   *
   * Bewusst über ALLE gewählten Prämien: Es dürfen mehrere gewählt werden (offer.maxBonusCount).
   * Zuvor hingen beide Weichen an der zuletzt hinzugefügten Prämie und wurden beim Abwählen einer
   * beliebigen Prämie pauschal zurückgesetzt - eine weiterhin gewählte Geldprämie verlor damit ihre
   * Zahldaten-Felder mitsamt der bereits eingegebenen IBAN.
   */
  private updateBonusPaymentFlags(): void {
    this.moneyBonusSelected = this.selectedBonus.some(bonus => isMoneyBonus(bonus?.bonusType));
    this.nonMoneyBonusWithClientCoPaymentSelected = this.selectedBonus.some(
      bonus => bonus?.bonusType === BonusType.NON_MONEY_BONUS && bonus?.clientCoPayment > 0);
    this.setValidatorsAtIBAN(this.moneyBonusSelected || this.nonMoneyBonusWithClientCoPaymentSelected);
  }

  onDeselectBonus(index: number): void {
    let bonus: Bonus = this.selectedBonus[index];
    (this.bonusFormGroup.get('bonus') as FormArray).removeAt(index);
    this.calcBonusPoints();
    this.selectedBonus.splice(index, 1);
    for (let i = 0; i < this.bonuses().length; i++) {
      if (bonus == this.bonuses()[i]) {
        this.bonuses()[i].selected = false;
        break;
      }
    }


    this.updateBonusPaymentFlags();

    this.handleBirthdayField();
    this.applyBonusAddressActivation();
  }

  /**
   * wählt alle boni ab
   * @private
   */
  private removeAllBonuses() {
    (this.bonusFormGroup.get('bonus') as FormArray).clear();
    this.calcBonusPoints();
    this.selectedBonus = [];
    for (let bonus of this.bonuses()) {
      bonus.selected = false;
    }
    this.updateBonusPaymentFlags();
  }

  calcBonusPoints(): void {
    let usedPoints = 0;

    (this.bonusFormGroup.get('bonus') as FormArray).controls.forEach((bonus) => {
      usedPoints += bonus.value.bonusValue;
    });

    this.usedPoints = usedPoints;
    this.freeBonusPoints = this.maxPoints - usedPoints;
  }

  /**
   * Macht die Adressfelder eines Prämien-Adressblocks zu Pflichtfeldern.
   *
   * Nötig, weil der Bonus-Step sonst gar nicht validiert: Die Pflichtfeld-Validatoren der geteilten
   * Adress-Komponente entstehen über deren [required]-Bindungen, und die hängen am
   * AddressFieldHandler, der hier ohne forProductType nicht greift. Ohne eigene Validatoren ist die
   * Gruppe immer gültig - der Step meldet sich als erledigt, obwohl z.B. die für eine Fremdprämie
   * nötige Werberadresse noch leer ist, und lässt sich überspringen.
   *
   * Ein nicht aktiver Block ist deaktiviert und zählt damit nicht in die Gültigkeit hinein
   * (s. applyBonusAddressActivation) - die Validatoren stören dort also nicht.
   */
  /** Der Anzeigetext zu einem Anrede-Schlüssel aus den Stammdaten; leer, wenn er dort nicht steht. */
  private salutationDescription(key: string): string {
    const salutations = this.baseDataService.getBaseData('addresscodes') ?? [];
    return salutations.find(entry => entry.key === key)?.description ?? '';
  }

  private requireAddressFields(addressGroup: FormGroup): void {
    ['person.salutation', 'person.firstName', 'person.lastName',
      'address.country', 'address.zipcode', 'address.city', 'address.street', 'address.houseNo']
      .forEach(path => {
        const control = addressGroup.get(path);
        control?.addValidators(Validators.required);
        // addValidators allein rechnet den Status des Controls NICHT neu - ohne das hier bliebe es
        // bis zum naechsten enable()/disable() faelschlich gueltig.
        control?.updateValueAndValidity({emitEvent: false});
      });

    addressGroup.updateValueAndValidity({emitEvent: false});
  }

  get bonusAddressGroup(): FormGroup {
    return this.bonusFormGroup.get('bonusAddress') as FormGroup;
  }

  get advertiserAddressGroup(): FormGroup {
    return this.bonusFormGroup.get('advertiserAddress') as FormGroup;
  }

  /**
   * Ist der Prämien-Adressblock aufgeklappt? Ob die Maske überhaupt gerendert wird, sagt
   * showBonusAddressForm(); ob sie offen ist, die Checkbox über das Control 'showAddress'.
   *
   * Dieses Prädikat MUSS derselben Bedingung folgen wie das Template - sonst validiert die
   * Komponente einen Block, den der Nutzer nicht sieht.
   */
  private isBonusAddressActive(): boolean {
    if (!this.showBonusAddressForm()) {
      return false;
    }
    return !!this.bonusAddressGroup.get('showAddress')?.value;
  }

  /** Die Werberadresse wird nur für eine Fremdprämie gerendert und hat keine Checkbox — s. isBonusAddressActive(). */
  private isAdvertiserAddressActive(): boolean {
    return this.showAdvertiserAddressBlock();
  }

  /**
   * Schaltet die Inhalts-Gruppen eines Adressblocks. Liefert true, wenn wirklich umgeschaltet wurde.
   *
   * Umgeschaltet wird nur bei echtem Zustandswechsel und ohne Events, weil ein No-Op-enable()/
   * disable() sonst auf jedem Kind-Control ein valueChanges emittiert (s. P5W-4383).
   */
  private applyContentGroupActivation(addressGroup: FormGroup, active: boolean): boolean {
    let switched = false;

    BONUS_CONTENT_GROUPS.forEach(groupName => {
      const contentGroup = addressGroup.get(groupName);
      if (!contentGroup || contentGroup.enabled === active) {
        return;
      }
      if (active) {
        contentGroup.enable({emitEvent: false});
      } else {
        contentGroup.disable({emitEvent: false});
      }
      switched = true;
    });

    return switched;
  }

  /**
   * Führt das Lieferziel der Prämie laufend aus dem Bestell-State nach.
   *
   * Muss reaktiv sein: der MatStepper erzeugt die Inhalte ALLER Schritte sofort (kein
   * 'ng-template matStepContent'), dieser Step läuft also schon durch ngOnInit, bevor der Nutzer im
   * Adress-Step überhaupt etwas eingegeben hat. Eine einmalige Auswertung sähe dort immer eine
   * leere Adresse und würde die Maske auch dann aufklappen, wenn später eine Adresse erfasst wird.
   */
  private observeBonusAddressTarget(): void {
    this.subscriptions.add(this.orderProcessService.getOrderState()
      .pipe(
        map(state => state?.addresses),
        distinctUntilChanged()
      )
      .subscribe(addresses => this.updateBonusAddressTarget(addresses)));
  }

  /**
   * Zeigt das Lieferziel an und klappt die Prämienadress-Maske auf, solange es keines gibt - dann
   * muss der Nutzer die Adresse selbst erfassen.
   *
   * Nicht angefasst wird der Zustand, sobald der Nutzer die Checkbox selbst bedient oder bereits
   * eine Prämienadresse eingegeben hat; sonst würde eine nachträglich erfasste Lieferadresse seine
   * Eingabe wieder zuklappen und damit verwerfen.
   */
  private updateBonusAddressTarget(addresses: OrderState['addresses']): void {
    const target = resolveBonusAddressTarget(addresses);
    const targetChanged = target !== this.bonusAddressTarget();
    this.bonusAddressTarget.set(target);

    if (this.bonusAddressChoiceByUser) {
      if (targetChanged) {
        this.publishBonusData();
      }
      return;
    }

    const showAddress = this.bonusAddressGroup.get('showAddress');
    const shouldBeOpen = !target;
    if (!!showAddress?.value === shouldBeOpen) {
      return;
    }
    if (!shouldBeOpen && !isAddressFormValueEmpty(this.bonusAddressGroup.getRawValue() as AddressFormValue)) {
      return;
    }

    showAddress?.setValue(shouldBeOpen, {emitEvent: false});
    this.applyBonusAddressActivation();
    this.publishBonusData();
  }

  /**
   * Ein zugeklappter oder gar nicht gerenderter Prämien-Adressblock ist fachlich nicht vorhanden und
   * darf deshalb weder validiert noch übertragen werden (P5W-4462, analog P5W-4409). Angular nimmt
   * deaktivierte Controls sowohl aus 'valid' als auch aus 'value' heraus — das deckt beides mit
   * einem Mechanismus ab, unabhängig davon, welche Validatoren die geteilte Adress-Komponente auf
   * den Kind-Controls hinterlässt (required auf person.firstName aus onSalutationChange(), der
   * Async-Validator auf userData.phone, die Pattern-Validatoren auf der Adresse).
   *
   * Das Control 'showAddress' der Prämienadresse bleibt bewusst aktiv: sonst wäre die Checkbox nicht
   * mehr bedienbar und die gesamte Gruppe DISABLED — was HupSubscriptionAddressComponent beim Init
   * wieder aufmachen würde. Die Werberadresse hat kein solches Control und wird im inaktiven Zustand
   * komplett DISABLED; das ist unkritisch, weil dieselbe Komponente sie genau dann wieder aktiviert,
   * wenn ihr Block gerendert wird — also in dem Zustand, in dem wir sie hier ohnehin aktivieren.
   *
   * Der Status wird einmal gebündelt auf der Wurzel neu berechnet, damit statusChanges genau ein
   * Ereignis liefert und connectForm genau einen State-Push macht.
   */
  private applyBonusAddressActivation(): void {
    const bonusSwitched = this.applyContentGroupActivation(this.bonusAddressGroup, this.isBonusAddressActive());
    const advertiserSwitched = this.applyContentGroupActivation(this.advertiserAddressGroup, this.isAdvertiserAddressActive());

    if (bonusSwitched || advertiserSwitched) {
      this.bonusFormGroup.updateValueAndValidity();
    }
  }


  protected readonly CssPropertiesEnum = CssPropertiesEnum;
  protected readonly BonusType = BonusType;
  protected readonly document = document;
}
